import { Box, Drawer, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Image from 'next/image';
import Link from 'next/link';
import { FC } from 'react';
import { shallow } from 'zustand/shallow';

import { LanguageSection } from '../header/language-section';
import { ProfileSection } from '../header/profile-section';

import { MenuList } from './menu-list';

import ConnexLogo from '@/assets/images/ConnexLogo.svg';
import Mouthwave from '@/assets/images/Mouthwave.svg';
import { DrawerButton } from '@/components/button/icon-button';
import { useUserPermissions } from '@/hooks/use-user-permissions';
import { useTelmaPortalStore } from '@/store/telma-portal-store';

export const Sidebar: FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [opened, setMenu] = useTelmaPortalStore(
        (state: { opened: boolean; setMenu: (isOpen: boolean) => void }) => [
            state.opened,
            state.setMenu,
        ],
        shallow,
    );
    const { isHgcConnex: isHgcUser } = useUserPermissions();
    const isNarrowBar = !opened && !isMobile;
    const logoSrc = isHgcUser ? ConnexLogo : Mouthwave;
    const logoAlt = isHgcUser ? 'HGC Connex logo' : 'Telma logo';
    const brandName = isHgcUser ? 'HGC Connex' : 'TelmaPortal';
    const headerSize = isHgcUser ? '1.8rem' : '2rem';
    const drawerWidth = (isNarrow: boolean) =>
        // @ts-expect-error
        isNarrow ? theme.spacing(8) : theme.mixins.drawer.width;

    const drawerContent = (
        <Box
            style={{
                boxSizing: 'border-box',
                background: theme.palette.primary.main,
                color: 'white',
                height: isNarrowBar
                    ? '100vh'
                    : isMobile
                    ? 'calc(100vh - 56px)'
                    : 'calc(100vh - 80px)',
                paddingLeft: isNarrowBar ? undefined : '16px',
                paddingRight: isNarrowBar ? undefined : '16px',
                //@ts-ignore
                width: `${theme.mixins.drawer.width}px`,
            }}
        >
            {isNarrowBar ? (
                <Box sx={{ p: 1, pl: 1.5 }}>
                    <DrawerButton />
                </Box>
            ) : (
                <Box sx={{ p: 2, pl: 1 }}>
                    <DrawerButton />
                </Box>
            )}
            <Link
                href='/bots'
                style={{ textDecoration: 'none', color: 'white' }}
            >
                <Box
                    component='div'
                    sx={{
                        p: isNarrowBar ? 1.5 : 2,
                        textAlign: isNarrowBar ? undefined : 'center',
                    }}
                >
                    <Image
                        src={logoSrc}
                        width={isNarrowBar ? 40 : drawerWidth(false) - 100}
                        alt={logoAlt}
                        priority
                    />
                    {!isNarrowBar && (
                        <h1 style={{ fontSize: headerSize }}>{brandName}</h1>
                    )}
                </Box>
            </Link>
            <MenuList isNarrowBar={isNarrowBar} />
            {isNarrowBar && (
                <>
                    <Box sx={{ mt: 5 }} />
                    <LanguageSection isNarrowBar={isNarrowBar} />
                    <ProfileSection isNarrowBar={isNarrowBar} />
                </>
            )}
        </Box>
    );

    return (
        <Box
            component='nav'
            sx={{
                flexShrink: { md: 0 },
                width: isMobile ? '100%' : 'auto',
            }}
        >
            <Drawer
                variant={isMobile ? 'temporary' : 'permanent'}
                anchor='left'
                open={isMobile ? opened : true}
                onClose={() => setMenu(false)}
                ModalProps={{ keepMounted: true }}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: drawerWidth(isNarrowBar),
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                        overflowX: 'hidden',
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        borderRight: 'none',
                        [theme.breakpoints.up('sm')]: {
                            width: drawerWidth(isNarrowBar),
                        },
                    },
                }}
            >
                {drawerContent}
            </Drawer>
        </Box>
    );
};
